/**
 * Signup.
 *
 * There is no region picker. There was one — US or EU, "this choice is permanent" — and with a
 * single cell it was a choice about nothing: a `us` signup reaching the one cell got a
 * `409 region_mismatch` it could never resolve (retarget-go-live.md §6). The cell decides the region
 * (bootstrap.ts: the cell's `REGION` wins, always), so the form states where the data lives and
 * promises nothing it cannot keep. The residency architecture is intact underneath — the column,
 * the immutability trigger, the cell-decides rule — and the picker returns when a second cell does.
 *
 * Failures are CLASSIFIED rather than flattened. One opaque sentence for everything was the right
 * instinct applied to the wrong set: it exists so the form cannot answer "does this address have an
 * account", and it ended up hiding the cell's breached-password refusal too — the one failure whose
 * cause the caller can actually act on. See `SignupFailure`.
 *
 * `VITE_CELL_REGION` is what this build's cell is labelled; it rides in the user's metadata only so
 * a development cell that declares no REGION has a real value to accept. Production ignores it in
 * favour of its own label — and when it is UNSET the metadata carries no region at all, which is
 * the one safe answer. See `CELL_REGION` below for why a default is worse than nothing.
 */
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Button, Callout, Card, Field, Icon } from '@flowza/ui'
import { configStatus, getClient } from '../../supabase'
import {
  PASSWORD_HINT,
  TOO_SHORT_MESSAGE,
  property,
  tooShort as isTooShort,
  weakPasswordMessage,
} from '../../auth/password-failure'
import { AuthLayout } from './AuthLayout'
import { MicrosoftButton } from './MicrosoftButton'

/**
 * The cell this build serves. A label the deployment declares, never a user's choice.
 *
 * `undefined` when the variable is unset, and deliberately NOT defaulted. It used to fall back to
 * `'us'`, which was safe only while no cell declared a region — the moment one does, a guess that
 * disagrees with it is fatal. `resolveRegion` (api/routes/bootstrap.ts) refuses a token asking for a
 * region other than the cell's with `409 region_mismatch`, and the provisioning screen renders that
 * as "this is ours to fix, not yours". The launch cell is labelled `in`. So a Pages build that
 * forgot this one variable would have sent every single new user a permanent, unexplained failure
 * on the first screen after signup — one missing variable, and nobody can create an account.
 *
 * Sending nothing is strictly better, because `resolveRegion` already has a correct branch for it:
 * where the cell declares a region and the token asks for none, the cell simply wins, which is the
 * documented rule everywhere else. Where no cell region is declared either, the API answers
 * `region_required` — a clear complaint about the deployment, rather than a tenant silently
 * labelled with a country nobody chose. `organizations.region` is immutable by trigger, so a wrong
 * label is not something a later fix can take back.
 */
const CELL_REGION: string | undefined =
  typeof import.meta.env.VITE_CELL_REGION === 'string' && import.meta.env.VITE_CELL_REGION !== ''
    ? (import.meta.env.VITE_CELL_REGION as string)
    : undefined

/**
 * What the caller is told when `signUp` fails.
 *
 * Every failure used to collapse into `uniform`, justified by the no-enumeration rule. That rule
 * is about the ADDRESS — "this email already has an account" is a membership oracle (security.md
 * §11) — and it swallowed a failure that is not about the address at all. The cell runs the
 * breached-password check security.md §2.1 specifies (HIBP k-anonymity, "minimum 12 chars, no
 * composition rules, every candidate checked against the breached corpus"), and GoTrue refuses a
 * pwned password with `422 weak_password`. The form then said "check them and try again" beside a
 * hint reading "At least 12 characters. No other rules" — so the one thing that had to change was
 * the one thing never named. The production auth log for 2026-09-09 is five of those 422s between
 * 14:05 and 15:40, every one of them "Password is known to be weak and easy to guess", against a
 * form that never once said the word password.
 *
 * A weak-password refusal is a fact about the string the caller just typed. It says nothing about
 * whether the address has an account, so it is not the oracle the uniform message exists to
 * prevent, and `uniform` keeps everything that is: `user_already_exists`, `email_exists`, and the
 * unrecognized default.
 */
export type SignupFailure =
  /** The password has to change, so the message belongs on the password field. */
  | { readonly kind: 'password'; readonly message: string }
  /** Not the caller's details — a limit or a deployment fault. Saying "check them" would be a lie. */
  | { readonly kind: 'notice'; readonly title: string; readonly message: string }
  /** The one neutral sentence, for everything that could distinguish a known address from a new one. */
  | { readonly kind: 'uniform' }

export function classifySignupFailure(error: unknown): SignupFailure {
  const code = property(error, 'code')

  const weak = weakPasswordMessage(error)
  if (weak !== null) return { kind: 'password', message: weak }

  // A limit, not a mistake. "Check them and try again" is actively wrong here: it sends somebody
  // whose details are perfectly good back to retype them, and every retype spends more of the
  // budget that is already exhausted.
  if (code === 'over_email_send_rate_limit') {
    return {
      kind: 'notice',
      title: 'This cell cannot send another email just yet',
      message:
        'Your details are fine. The confirmation email allowance is spent for the moment — wait a few minutes and try again.',
    }
  }
  if (code === 'over_request_rate_limit') {
    return {
      kind: 'notice',
      title: 'Too many attempts',
      message: 'Your details are fine. Wait a minute, then try again.',
    }
  }

  // The deployment's fault, said in the words provision.tsx already uses for the same situation.
  if (code === 'signup_disabled' || code === 'email_provider_disabled') {
    return {
      kind: 'notice',
      title: 'This cell is not accepting new accounts',
      message: 'This is ours to fix, not yours — please get in touch.',
    }
  }

  return { kind: 'uniform' }
}

type Outcome =
  | { readonly kind: 'idle' }
  | { readonly kind: 'busy' }
  /** The account exists but Supabase wants the address confirmed before it issues a session. */
  | { readonly kind: 'confirm'; readonly email: string }
  | { readonly kind: 'failed'; readonly failure: SignupFailure }

export default function Signup() {
  const navigate = useNavigate()
  const [password, setPassword] = useState('')
  const [outcome, setOutcome] = useState<Outcome>({ kind: 'idle' })
  const config = configStatus()

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const client = getClient()
    // A build with no Supabase variables used to return here in silence: the button stayed
    // enabled, pressed cleanly, and did nothing at all, forever. The callout below says which
    // variables are missing and the button is disabled, exactly as Login already does.
    if (client === null) return
    const email = String(new FormData(event.currentTarget).get('email') ?? '').trim()
    setOutcome({ kind: 'busy' })
    // The cell's label rides in the user's metadata for the personal-organization provisioning the
    // API performs (multi-tenancy.md §3); the browser never creates an organization itself, and a
    // production cell overrides this with its own REGION regardless.
    const { data, error } = await client.auth.signUp({
      email,
      password,
      // Omitted entirely when unset, rather than sent as a guess: the cell's own label is the
      // truthful answer and `resolveRegion` prefers it over anything the token asks for.
      options: { data: CELL_REGION === undefined ? {} : { region: CELL_REGION } },
    })
    if (error) {
      // "Already registered" still gets the one neutral message — a signup form that says "this
      // address has an account" is the same membership oracle as a login form that does
      // (security.md §11). What `classifySignupFailure` pulls out of that bucket is the set of
      // failures that are not about the address at all, and so cannot enumerate anybody.
      setOutcome({ kind: 'failed', failure: classifySignupFailure(error) })
      return
    }
    if (data.session) {
      navigate('/app', { replace: true })
      return
    }
    setOutcome({ kind: 'confirm', email })
  }

  // Client-side length only. The breach check and the real rule live on the server: a password
  // policy enforced in the browser is a suggestion. What the browser owes the caller is the
  // server's ANSWER, which is what `passwordError` carries once a submission comes back.
  const tooShort = isTooShort(password)
  const failure = outcome.kind === 'failed' ? outcome.failure : null
  const passwordError = tooShort
    ? TOO_SHORT_MESSAGE
    : failure?.kind === 'password'
      ? failure.message
      : null

  return (
    <AuthLayout
      title="Create your account"
      subtitle="You will get a personal organization straight away. You can create team organizations later."
      footer={
        <>
          Already have an account?{' '}
          <Link to="/auth/login" className="text-accent font-medium">
            Sign in
          </Link>
        </>
      }
    >
      {/*
        BOTH branches, because this screen cannot tell which happened and must not pretend.

        Supabase answers a signup for an address that ALREADY HAS AN ACCOUNT with exactly the shape
        it answers a new one: no error, no session. That is deliberate — it is what stops a signup
        form being used to discover who has an account (security.md §11) — and it means "we sent a
        confirmation link" is a claim this code cannot support. Nothing is sent to an address that
        already has an account, so the founder's 2026-09-12 report is what that copy produces every
        time: a person waiting for a message that was never going to arrive, with no way onward.

        Naming both possibilities keeps the property the neutral message exists for (the reader
        learns nothing about anybody else's address) while giving the one person who matters — the
        one reading it — something to do either way.
      */}
      {outcome.kind === 'confirm' ? (
        <div className="mb-4">
          <Callout tone="info" title="Check your email, or sign in">
            If {outcome.email} is new to FlowZa, we sent it a confirmation link — open it, then sign
            in. If that address already has an account, nothing was sent:{' '}
            <Link to="/auth/login" className="text-accent font-medium">
              sign in
            </Link>{' '}
            with it instead, or{' '}
            <Link to="/auth/reset" className="text-accent font-medium">
              reset the password
            </Link>
            .
          </Callout>
        </div>
      ) : null}
      {!config.ok ? (
        <div className="mb-4">
          <Callout tone="danger" title="This deployment is not configured">
            Missing build variables: {config.missing.join(', ')}. Creating an account needs them.
          </Callout>
        </div>
      ) : null}
      {failure?.kind === 'notice' ? (
        <div className="mb-4">
          <Callout tone="danger" title={failure.title}>
            {failure.message}
          </Callout>
        </div>
      ) : null}
      {/*
        The uniform message, and ONLY for the failures that could enumerate an address. A
        weak-password refusal is rendered on the password field instead, where the input that has
        to change is — telling somebody to "check those details" when the details are fine and the
        password is the problem is how five refusals in a row read as a broken product.
      */}
      {failure?.kind === 'uniform' ? (
        <div className="mb-4">
          <Callout tone="danger" title="That did not work">
            We could not create an account with those details. Check them and try again.
          </Callout>
        </div>
      ) : null}

      <Card>
        <form className="flex flex-col gap-4" onSubmit={(e) => void onSubmit(e)}>
          <Field label="Work email" type="email" name="email" autoComplete="email" required />
          <Field
            label="Password"
            type="password"
            name="password"
            autoComplete="new-password"
            required
            hint={PASSWORD_HINT}
            value={password}
            onChange={(e) => {
              setPassword(e.currentTarget.value)
              // The server's verdict was about the password that was submitted. Once that string
              // changes the verdict is stale, and a refusal left standing under a field the caller
              // has already fixed is its own dead end (ux.md §12).
              if (failure?.kind === 'password') setOutcome({ kind: 'idle' })
            }}
            {...(passwordError ? { error: passwordError } : {})}
          />

          {/*
            No roadmap promises. This said "dedicated US and EU residency regions are on the
            roadmap" until the founder set a twelve-month horizon on both (CLAUDE.md §0), which
            turned a near-term plan into a claim about something that will not exist for a year —
            the same class of defect as the three untrue login-page claims already fixed once. What
            is left is the part that is true today and matters most: the choice is permanent.
          */}
          <Callout tone="info" title="Where your data lives">
            Your organization&rsquo;s documents, evidence and backups are stored in the FlowZa Sign
            cell you are signing up on, and that location cannot be changed afterwards.
          </Callout>

          <Button
            type="submit"
            disabled={tooShort || outcome.kind === 'busy' || !config.ok}
            className="mt-1 w-full"
          >
            {outcome.kind === 'busy' ? 'Creating…' : 'Create account'}
          </Button>

          {/*
            Turnstile mounts here — signup ALWAYS, per security.md §12. It is deliberately absent
            from the signing ceremony, where a challenge would be a WCAG 2.2 AA problem and a
            conversion problem at the worst possible moment.
          */}
          {/*
            This line used to read "Protected by a bot check." It is not: the comment above is the
            only Turnstile in the repository. The second half was true and is kept, because it is a
            promise about the CEREMONY that the code does keep — there is no third-party challenge
            script anywhere in the signing surface, deliberately (Ceremony.tsx's header).
          */}
          <p className="text-text-muted m-0 flex items-center gap-1.5 text-sm">
            <Icon name="lock" size={14} />
            We never send a challenge inside a signing session.
          </p>
        </form>

        {/*
          Signing up with Microsoft is the same call as signing in with it (ADR 0093) — the account
          is created if there is none — so the component is shared and only this sentence differs.
          It renders only where the project has the provider enabled.
        */}
        <MicrosoftButton
          intro="Or start with the account you already sign in to work with — no password to choose."
          disabled={!config.ok}
        />
      </Card>
    </AuthLayout>
  )
}
